import React, { useState, useMemo } from 'react';
import { useInventory } from '../context/InventoryContext';
import { useAuth } from '../context/AuthContext';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import Pagination from '../components/Pagination';

const AdminSignup = () => {
  const { companies = [] } = useInventory();
  const { users = [], addUser, deleteUser, updateUser } = useAuth(); 
  
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false); 
  const [editingUserId, setEditingUserId] = useState(null); 

  const [form, setForm] = useState({
    username: '', email: '', password: '', companyId: ''
  });
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');

  // Notification functions
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 3000);
  };

  // 1. Get only admin users
  const admins = useMemo(() => {
    return users.filter(u => String(u.role).trim().toLowerCase() === "admin");
  }, [users]);

  // 2. Apply search filter
  const filteredAdmins = useMemo(() => {
    return admins.filter(admin => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const companyName = companies.find(
        c => String(c.id) === String(admin.companyId)
      )?.name || '';
      return (
        admin.username.toLowerCase().includes(q) ||
        (admin.email || '').toLowerCase().includes(q) ||
        companyName.toLowerCase().includes(q)
      );
    });
  }, [admins, searchQuery, companies]);

  // 3. Paginate
  const totalItems = filteredAdmins.length;
  const paginatedAdmins = useMemo(() => {
    return filteredAdmins.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [filteredAdmins, currentPage, pageSize]);

  const resetForm = () => {
    setForm({ username: '', email: '', password: '', companyId: '' });
    setError('');
    setIsEditing(false);
    setEditingUserId(null);
  };

  const handleEditClick = (admin) => {
    setError('');
    setIsEditing(true);
    setEditingUserId(admin.id);
    setForm({
      username: admin.username,
      email: admin.email || '',
      companyId: admin.companyId || '',
      password: '' // Explicitly empty so it is clear it isn't mandatory
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEditing) {
      if (!form.username.trim() || !form.email.trim() || !form.companyId) {
        setError('Username, Email, and Company fields are required.');
        return;
      }
    } else {
      if (!form.username.trim() || !form.email.trim() || !form.password || !form.companyId) {
        setError('All fields are required.');
        return;
      }
    }

    const usernameExists = users.some(u => u.username === form.username && u.id !== editingUserId);
    if (usernameExists) {
      setError('Username already exists.');
      return;
    }

    if (isEditing) {
      const updatedData = {
        username: form.username.trim(),
        email: form.email.trim(),
        companyId: form.companyId,
        role: 'admin'
      };
      
      // Password field is completely optional—appended only if typed out
      if (form.password && form.password.trim() !== '') {
        updatedData.password = form.password;
      }

      if (updateUser) {
        updateUser(editingUserId, updatedData);
        showNotification(`Admin "${form.username.trim()}" has been updated successfully`, 'success');
      } else {
        console.warn("updateUser engine is broken or missing from context setup.");
      }
    } else {
      addUser({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        companyId: form.companyId,
        role: 'admin'
      });
      showNotification(`Admin "${form.username.trim()}" has been added successfully`, 'success');
    }

    resetForm();
    setShowForm(false);
  };

  const handleDelete = (id) => {
    const adminToDelete = users.find(u => u.id === id && String(u.role).trim().toLowerCase() === "admin");
    if (adminToDelete && window.confirm(`Are you sure you want to delete admin "${adminToDelete.username}"?`)) {
      deleteUser(id, "admin");
      showNotification(`Admin "${adminToDelete.username}" has been deleted`, 'error');
    }
  };

  return (
    <div>
      {/* Notification */}
      {notification.visible && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Admin Users</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
          <FaPlus /> Create Company Admin
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by username, email, company…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedAdmins.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-8 text-center text-sm text-gray-500">
                    No admins created yet.
                  </td>
                </tr>
              ) : (
                paginatedAdmins.map(admin => (
                  <tr key={admin.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{admin.username}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{admin.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {companies.find(c => String(c.id) === String(admin.companyId))?.name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center space-x-3">
                      <button 
                        onClick={() => handleEditClick(admin)} 
                        className="text-indigo-600 hover:text-indigo-900 transition"
                        title="Edit Admin"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      
                      <button 
                        onClick={() => handleDelete(admin.id)} 
                        className="text-red-500 hover:text-red-700 transition"
                        title="Delete Admin"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* Modal Form Dialog */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {isEditing ? "Update Company Admin" : "Create Company Admin"}
            </h2>
            {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.username}
                  onChange={e => setForm({...form, username: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEditing ? "New Password (Leave blank to keep unchanged)" : "Password"}
                </label>
                <input
                  type="password"
                  placeholder={isEditing ? "••••••••" : ""}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  required={!isEditing} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  value={form.companyId}
                  onChange={e => setForm({...form, companyId: e.target.value})}
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">
                  {isEditing ? "Save Changes" : "Create Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSignup;